import {handleAddToCart} from '@/cart/actions';
import {getCart} from '@/cart/cart-store';
import AnimatedCollapsingBox from '@/components/AnimatedHeightBox';
import ProductImageCarousel from '@/components/buy-stack/ProductImageCarousel';
import RatingWidget from '@/components/RatingWidget';
import CarouselImagePreviewSquare from '@/experiments/single-step-buy-stack/CarouselImagePreviewSquare';
import IconValueProps, {
  valueProps,
} from '@/experiments/single-step-buy-stack/IconValueProps';
import PackSelector from '@/experiments/single-step-buy-stack/PackSelector';
import PlanSelector from '@/experiments/single-step-buy-stack/PlanSelector';
import SubscriptionSummary from '@/experiments/single-step-buy-stack/SubscriptionSummary';
import VariantSelector from '@/experiments/single-step-buy-stack/VariantSelector';
import logo from '@/icons/EB_Logo_Vector_Blue.svg';
import {useLocale} from '@/localization/use-locale';
import {isRecurringPlan} from '@/product/create-delivery-options';
import {preloadProductCarouselImages} from '@/product/preload-product-carousel-images';
import {getProductSelection, selectPlan} from '@/product/product-selection';
import {useEffect} from 'preact/hooks';
import {BuyStackVariant} from './singleStepBuyStackRegister';

const ctaButtonLabelSubscribe = 'ADD TO CART';
const ctaButtonLabelOnetime = 'ADD TO CART';
const ratingLink = '#reviews';
const placeholderImage = {
  url: logo,
  alt: 'placeholder',
};
const onetimePurchaseLabel = 'One-Time Purchase';
const chooseYourSizeLabel = 'Choose Your Size';

export default function SingleStepBuyStack({
  buyStackVariant,
  title,
}: SingleStepBuyStackProps) {
  // Conditionally hide recurring options for single-step OTP
  // and single-step loads variants
  const shouldShowRecurringOptions = ![
    BuyStackVariant.SingleStepOtp,
    BuyStackVariant.SingleStepLoads,
  ].includes(buyStackVariant);

  const singleStepLoadsFlag =
    buyStackVariant === BuyStackVariant.SingleStepLoads;

  const {monthlyPlans, onetimePlan, product, selectedPlan, selectedVariant} =
    getProductSelection();
  const images = product
    ? preloadProductCarouselImages({product, selectedVariant})
    : [];

  const featured = images[0] ?? placeholderImage;
  const rating = product?.average_rating ?? 4.5;
  const totalReviews = product?.number_of_reviews ?? 56022;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const productTitle = (title || product?.title) ?? '';

  const isRecurring = !!selectedPlan && isRecurringPlan(selectedPlan);
  const ctaButtonLabel = isRecurring
    ? ctaButtonLabelSubscribe
    : ctaButtonLabelOnetime;

  const cart = getCart();
  const currency = cart?.currency ?? 'USD';
  const locale = useLocale();

  // Auto-select onetime plan if recurring options are hidden
  useEffect(() => {
    if (!shouldShowRecurringOptions) {
      selectPlan(onetimePlan);
    } else {
      selectPlan(monthlyPlans[0] ?? onetimePlan);
    }
  }, [shouldShowRecurringOptions, onetimePlan, monthlyPlans]);

  return !!product ? (
    <div className="mx-auto grid max-w-5xl grid-cols-1 gap-x-28 gap-y-8 px-4 py-8 text-deep-ocean-blue md:grid-cols-2 md:px-16 md:py-12">
      <ProductImageCarousel
        images={images}
        featured={featured}
        showArrows={false}
        customPaging={({image, isActive}) => (
          <CarouselImagePreviewSquare image={image} isActive={isActive} />
        )}
        showPagingOnMobile={true}
      />
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col gap-y-4">
          <div className="flex items-start gap-3">
            <RatingWidget
              size="1.25rem"
              rating={rating}
              totalReviews={totalReviews}
              showText={false}
            />
            <a href={ratingLink}>
              {Intl.NumberFormat(locale).format(totalReviews)} Reviews
            </a>
          </div>
          <div className="flex flex-col gap-y-2">
            <h3 className="whitespace-break-spaces">{productTitle}</h3>
            <VariantSelector />
          </div>
          <div className="grid grid-cols-1 gap-y-2">
            {shouldShowRecurringOptions && (
              <PlanSelector
                formatMoneyWithDecimals={false}
                currency={currency}
                locale={locale}
              />
            )}
            <PackSelector
              formatMoneyWithDecimals={false}
              onetimePurchaseLabel={
                singleStepLoadsFlag ? chooseYourSizeLabel : onetimePurchaseLabel
              }
              currency={currency}
              locale={locale}
            />
          </div>
        </div>
        {!singleStepLoadsFlag && <IconValueProps valueProps={valueProps} />}
        <div className="flex flex-col gap-y-6">
          <button
            data-testId="buy-stack-add-to-cart"
            className="btn btn-lg btn-primary w-full"
            onClick={() => void handleAddToCart(selectedVariant, selectedPlan)}
          >
            {ctaButtonLabel}
          </button>
          {singleStepLoadsFlag && <IconValueProps valueProps={valueProps} />}
          <AnimatedCollapsingBox
            isCollapsed={!isRecurring}
            className="duration-500 ease-in-out"
          >
            <SubscriptionSummary currency={currency} locale={locale} />
          </AnimatedCollapsingBox>
        </div>
      </div>
    </div>
  ) : null;
}

export type SingleStepBuyStackProps = {
  buyStackVariant: BuyStackVariant;
  title?: string;
};
